import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, Link, TextField, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { CustomEvent } from "schemas/functions";

import StyledCard from "components/Styled/Card";
import QuestionChip from "components/Styled/QuestionChip";
import { AFFILIATE_TERMS_AND_CONDITIONS_LINK } from "constants/influencer";
import { fetcherAuth } from "utils/api";
import { normalizePhone } from "utils/form";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import StyledOnboardingPage from "../StyledOnboardingPage";
import styles from "../styles";

interface Props {
  handleBack: () => void;
  handleSkip?: () => void;
  handleNext: () => void;
}

const FinalOptInForm = ({ handleSkip, handleNext, handleBack }: Props) => {
  const {
    currentOrg,
    setUserNotifications,
    userNotifications,
    profile,
    setProfile,
  } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const [joinedAffiliate, setJoinedAffiliate] = useState<boolean>(true);

  const handleSelect = (selectedOption: string) => {
    setUserNotifications((prev) => {
      if (!prev) {
        prev = {};
      }
      const copy = makeDeepCopy(prev);
      copy[selectedOption] = !copy[selectedOption];
      return copy;
    });
  };

  const handleChangePhone = (e: CustomEvent) => {
    const { value } = e.target;
    const copy = makeDeepCopy(profile);
    copy.phoneNumber = value;
    setProfile(copy);
  };

  const submitForm = async () => {
    if (userNotifications?.optInPhone && !profile?.phoneNumber) {
      setAlert(
        "Please provide a phone number to receive text message notifications.",
        "warning",
      );
      return;
    }
    handleNext();
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings`,
        "PUT",
        {},
        {
          optInPhone: userNotifications?.optInPhone,
          optInEmail: userNotifications?.optInEmail,
          phoneNumber: profile?.phoneNumber,
        },
      );
      if (joinedAffiliate) {
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/affiliate-organizations`,
          "POST",
        );
      }
    } catch (error) {
      setErrorAlert(error);
      handleBack();
    }
  };

  useEffect(() => {
    if (
      !userNotifications?.optInPhone &&
      !profile?.phoneNumber &&
      !userNotifications?.optInEmail
    ) {
      setUserNotifications((prev) => {
        if (!prev) {
          prev = {};
        }
        const copy = makeDeepCopy(prev);
        copy.optInEmail = true;
        copy.optInPhone = true;
        return copy;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StyledOnboardingPage
        title="Notifications & Affiliate Settings 🔔"
        stepNumber={3}
        handleBack={handleBack}
        handleSkip={handleSkip}
        handleNext={submitForm}
      >
        <StyledCard styles={{ px: 3, py: 4 }}>
          <Box sx={styles.questionBox}>
            <Typography gutterBottom>
              How would you like to receive notifications and updates about your
              account?
            </Typography>

            <Typography variant="caption" color="textSecondary" paragraph>
              Select all that apply.
            </Typography>

            <Grid container gap={1}>
              <QuestionChip
                label="Email"
                isSelected={!!userNotifications?.optInEmail}
                onClick={() => handleSelect("optInEmail")}
              />
              <QuestionChip
                label="Phone"
                isSelected={!!userNotifications?.optInPhone}
                onClick={() => handleSelect("optInPhone")}
              />
              {userNotifications?.optInPhone && (
                <TextField
                  value={normalizePhone(profile?.phoneNumber || "")}
                  onChange={handleChangePhone}
                  name="phoneNumber"
                  required
                  variant="standard"
                  placeholder="Phone number"
                />
              )}
            </Grid>
          </Box>

          <Box sx={styles.questionBox}>
            <Typography gutterBottom>
              Would you be interested in becoming an affiliate for Bento to earn
              extra income?
            </Typography>

            <Typography variant="caption" color="textSecondary" paragraph>
              <Link
                onClick={() => {
                  trackEvent("Affiliate Terms and Conditions Clicked");
                }}
                href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}
                target="_blank"
                rel="noreferrer"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                Terms & Conditions Apply{" "}
              </Link>
            </Typography>

            <Grid container gap={1}>
              <QuestionChip
                label="Yes"
                isSelected={joinedAffiliate}
                onClick={() => setJoinedAffiliate(true)}
              />
              <QuestionChip
                label="Not Interested"
                isSelected={!joinedAffiliate}
                onClick={() => setJoinedAffiliate(false)}
              />
            </Grid>
          </Box>
        </StyledCard>
      </StyledOnboardingPage>
    </>
  );
};

export default FinalOptInForm;
